import { isFunction } from 'lodash';
import { useSession } from 'next-auth/react';
import { DateRange } from 'react-day-picker';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { tv } from 'tailwind-variants';

import { Heading } from '@/components/heading';
import { ActiveScheduleFilters } from '@/components/modules/schedules/active-filters';
import { TimeMarkers } from '@/components/route-segment-panel/segment-schedules/time-markers';
import { getScheduleChartData } from '@/components/route-segment-panel/segment-schedules/utils/get-schedule-chart-data';
import { generateScheduleKey } from '@/components/route-segment-panel/utils';
import { SearchDirection } from '@/components/schedule-search-form';
import { useScheduleFilters } from '@/hooks/use-schedule-filters';
import { MODE_WATER, USER_ACCESS_FEATURES } from '@/lib/constants';
import { SpotRateModel } from '@/lib/models/rates/types';
import { transformRouteScheduleToItinerary } from '@/lib/transformers/itinerary';
import { featuresState } from '@/state/features';
import { selectedItineraryState } from '@/state/itinerary';
import { linesState } from '@/state/lines';
import { getScheduleQueryByIdState } from '@/state/schedules';
import { ActiveFilterOptions, FilterSelection, RouteScheduleInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { canAccessFeature } from '@/utils/auth';
import { getPreferredIdentifier } from '@/utils/helpers';
import { filterStandaloneScheduleOptions } from '@/utils/helpers/filter-route-options';

import { NoResultsRow } from '../shared/no-results-row';
import { RouteScheduleRow } from './schedule-row';
import { TimelineBar } from './timeline-bar';
import { getSortedAndFilterSchedules } from './utils/get-sorted-and-filtered-schedules';

export interface SegmentSchedulesProps {
  schedules: RouteScheduleInterface[];
  mode: SegmentModeType;
  customDateRange?: DateRange;
  direction: SearchDirection;
  isCompactView?: boolean;
  onResetFilters?: () => void;
  activeFilters?: FilterSelection;
  activeFilterOptions?: ActiveFilterOptions;
}

const SegmentSchedules = ({
  schedules,
  mode,
  customDateRange,
  direction,
  activeFilters = {},
  onResetFilters,
  isCompactView = false,
  activeFilterOptions,
}: SegmentSchedulesProps) => {
  const { data: session } = useSession();
  const canAccessRates = canAccessFeature(USER_ACCESS_FEATURES.RATES, session);
  const features = useRecoilValue(featuresState);

  const { updateFilters, resetAllFilters } = useScheduleFilters();
  const lineDictionary = useRecoilValue(linesState);
  const setSelectedItinerary = useSetRecoilState(selectedItineraryState);

  const shouldShowRates = features.showSpotRates && canAccessRates && mode === MODE_WATER;

  // Get Origin and Dest from returned results
  const firstResult = schedules[0];
  const origin = firstResult.from;
  const destination = firstResult.to;

  const { shouldShowTimezones, rangeStartTime, timeRangeHours, bars } = getScheduleChartData({
    mode,
    schedules,
    customDateRange,
    isCompactView,
  });

  const fromCode = getPreferredIdentifier(origin.identifiers, true)?.value || '';
  const toCode = getPreferredIdentifier(destination.identifiers, true)?.value || '';
  const scheduleKey = generateScheduleKey(fromCode, toCode);
  const { arriveHour, departHour } = useRecoilValue(getScheduleQueryByIdState(scheduleKey));
  const isArriveByDate = direction === 'arrive';

  const { filteredSchedules, emissionStats } = getSortedAndFilterSchedules({
    schedules,
    filters: activeFilters,
    filteringFunc: filterStandaloneScheduleOptions,
    isArriveByDate,
  });

  const handleOnRowClick = (row: RouteScheduleInterface, rates?: SpotRateModel[]) => {
    const itinerary = transformRouteScheduleToItinerary(row, lineDictionary, rates);
    setSelectedItinerary(itinerary);
  };

  const handleResetFilters = () => {
    if (isFunction(onResetFilters)) {
      onResetFilters();
    }
  };

  const { base, schedulesWrapper, mobileTitle } = styles({
    shouldShowTimezones,
  });

  return (
    <>
      <ActiveScheduleFilters
        activeFilterOptions={activeFilterOptions}
        activeFilters={activeFilters}
        onRemoveItem={updateFilters}
        onRemoveAllFilters={resetAllFilters}
      />
      <div className={base()}>
        <Heading className={mobileTitle()}>Search results</Heading>
        <TimelineBar
          mode={mode}
          bars={bars}
          origin={origin}
          destination={destination}
          shouldShowTimezones={shouldShowTimezones}
          shouldShowRates={shouldShowRates}
        />
        <div className={schedulesWrapper()}>
          {filteredSchedules.map((option, index) => (
            <RouteScheduleRow
              key={option.id}
              option={option}
              index={index}
              timeRangeHours={timeRangeHours}
              rangeStartTime={rangeStartTime}
              onRowClick={handleOnRowClick}
              emissionStats={emissionStats}
              shouldShowRates={shouldShowRates}
            />
          ))}
        </div>
        {customDateRange?.to && customDateRange?.from ? (
          <TimeMarkers
            timeRangeHours={timeRangeHours}
            origin={origin}
            destination={destination}
            dateRange={customDateRange}
            departHour={departHour}
            arriveHour={arriveHour}
            className={shouldShowTimezones ? 'top-1.5' : '-top-4'}
          />
        ) : null}
        {!filteredSchedules.length && (
          <NoResultsRow
            message="No schedules found that matched your filter selection."
            onResetFilters={handleResetFilters}
          />
        )}
      </div>
    </>
  );
};

const styles = tv({
  slots: {
    base: 'relative pb-6',
    schedulesWrapper: 'mx-2 flex flex-col gap-2',
    mobileTitle: 'px-4 md:hidden',
  },
  variants: {
    shouldShowTimezones: {
      true: {
        schedulesWrapper: 'md:pt-[3.5rem]',
      },
      false: { schedulesWrapper: 'md:pt-8' },
    },
  },
});

export { SegmentSchedules };
